import React, { Fragment, useMemo, useState } from 'react';
import Drawer from '../Common/Drawer/Drawer';
import Notification from '../Common/Notification';
import InputText from '../Common/InputText';
import { useUpdateResponsibleUser } from '../../apis/locationTasks';
import type { ResponsibleUser } from '../../apis/locationTasks/locationTask.types';
import './TaskListPage/TaskListPage.scss';

interface BulkUpdateResponsibleUserDrawerProps {
    responsibleUsers: ResponsibleUser[];
    onComplete: () => void;
    onCancel: () => void;
    locationKeys?: string[];
    locationTasks: Array<{ id: number }>;
    userId: number;
}

export const BulkUpdateResponsibleUserDrawer = ({
    responsibleUsers,
    onComplete,
    onCancel,
    locationTasks,
    userId,
}: BulkUpdateResponsibleUserDrawerProps) => {
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFormValid, setIsFormValid] = useState(true);
    const [assignedToMe, setAssignedToMe] = useState(false);

    const resultUsers = useMemo(() => {
        if (!searchTerm) return responsibleUsers;
        const lower = searchTerm.toLowerCase();
        return responsibleUsers.filter((u) => u.name.toLowerCase().indexOf(lower) > -1);
    }, [responsibleUsers, searchTerm]);

    const { mutate: updateResponsibleUser, isPending } = useUpdateResponsibleUser(() => {
        Notification('success', 'Location Tasks Updated', 'Responsible User has been set');
        onComplete();
    });

    const onSave = () => {
        if (!selectedUserId) {
            setIsFormValid(false);
            return;
        }
        if (isPending) return;
        updateResponsibleUser(
            { locationTaskIds: locationTasks.map((t) => t.id), responsibleUserId: selectedUserId },
            { onError: (err) => Notification('error', 'Error', err.message) }
        );
    };

    return (
        <Drawer isOpen={true} isWide={false} title='Update Responsible User'
            onSaveClick={onSave}
            saveText={isPending ? 'Saving...' : 'Save'}
            onCancelClick={onCancel}>
            <Fragment>
                {selectedUserId === userId && assignedToMe ? (
                    <div>
                        <p>These Tasks will be assigned to <u>you</u></p>
                        <span className='task-name' onClick={() => { setAssignedToMe(false); setSelectedUserId(null); }}>
                            Choose someone else
                        </span>
                    </div>
                ) : (
                    <Fragment>
                        <div>
                            <span className='task-name' onClick={() => { setAssignedToMe(true); setSelectedUserId(userId); }}>
                                Assign Tasks to Me
                            </span>
                            <br /><br />
                            <p>Or</p>
                        </div>
                        <InputText
                            type='text'
                            name='search'
                            label='User Search'
                            placeholder='Type a name here and press enter to search'
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.currentTarget.value)}
                            maxLength={20}
                        />

                        <form noValidate className={isFormValid ? '' : 'was-validated'}>
                            <div className='mb-3'>
                                <span className='has-float-label'>
                                    <select
                                        name='searchResults'
                                        size={10}
                                        className='form-control user-select'
                                        onChange={(e) => setSelectedUserId(parseInt(e.currentTarget.value))}
                                        required={true}>
                                        {resultUsers.map((user) => (
                                            <option key={user.id} value={user.id}>{user.name}</option>
                                        ))}
                                    </select>
                                    <label htmlFor='searchResults'>Search Results</label>
                                    <div className='invalid-feedback'>Choose a User</div>
                                </span>
                            </div>
                        </form>
                    </Fragment>
                )}
            </Fragment>
        </Drawer>
    );
};

export default BulkUpdateResponsibleUserDrawer;
